import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useLayoutEffect, useRef } from "react";
let pendingClose = null;
const FOCUSABLE = "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]";
export function FormDialog({ title, children, actions, initialFocus, onEscape, onScrim, width = 480, onEnter, ...data }) {
    const frame = useRef(null);
    const handlers = useRef({ onEscape, onScrim, onEnter });
    handlers.current = { onEscape, onScrim, onEnter };
    const titleId = useId();
    // Who had focus when this dialog opened, kept across StrictMode's effect re-run.
    const opener = useRef(null);
    useLayoutEffect(() => {
        const node = frame.current;
        if (node === null)
            return;
        const handoff = pendingClose;
        pendingClose = null;
        if (handoff?.node === node) {
            // StrictMode re-ran this effect: the teardown never happened, so the dialog is still
            // open with focus inside it, and no field was blurred.
            handoff.cancelled = true;
        }
        else if (handoff !== null) {
            // Another dialog closed in the same commit as this one opened: its opener becomes ours,
            // and it does not move focus out from under us.
            handoff.restore = false;
            opener.current = handoff.opener;
        }
        else {
            opener.current = document.activeElement;
        }
        if (!node.open && typeof node.showModal === "function")
            node.showModal();
        if (handoff?.node !== node)
            node.querySelector(initialFocus)?.focus();
        return () => {
            // Deferred one microtask: a StrictMode re-run mounts again before it runs and cancels it,
            // so closing (which blurs the focused field) and the restore happen only on a real close.
            const close = { node, opener: opener.current, cancelled: false, restore: true };
            pendingClose = close;
            queueMicrotask(() => {
                if (pendingClose === close)
                    pendingClose = null;
                if (close.cancelled)
                    return;
                if (node.open)
                    node.close();
                const back = close.opener;
                if (close.restore && back instanceof HTMLElement && back.isConnected && !back.matches(":disabled"))
                    back.focus();
            });
        };
        // Once per opening: the focus target is read when the dialog mounts.
    }, []);
    useEffect(() => {
        const node = frame.current;
        if (node === null)
            return;
        const cancel = (event) => {
            event.preventDefault();
            handlers.current.onEscape?.();
        };
        node.addEventListener("cancel", cancel);
        return () => node.removeEventListener("cancel", cancel);
    }, []);
    return (_jsx("dialog", { ref: frame, "data-dialog": "", ...data, "aria-labelledby": titleId, onClick: (event) => {
            // A click whose target is the dialog box itself landed on the backdrop: the box's
            // content fills it edge to edge.
            if (event.target === event.currentTarget)
                handlers.current.onScrim?.();
        }, onKeyDown: (event) => {
            if (event.key === "Enter" && handlers.current.onEnter !== undefined) {
                const target = event.target;
                if (target.closest("button, a[href], textarea") === null) {
                    event.preventDefault();
                    handlers.current.onEnter();
                }
                return;
            }
            if (event.key !== "Tab")
                return;
            const stops = Array.from(frame.current?.querySelectorAll(FOCUSABLE) ?? []);
            if (stops.length === 0)
                return;
            const edge = event.shiftKey ? stops[0] : stops[stops.length - 1];
            if (document.activeElement !== edge)
                return;
            event.preventDefault();
            (event.shiftKey ? stops[stops.length - 1] : stops[0]).focus();
        }, style: {
            width, maxWidth: "calc(100% - var(--space-4))", padding: 0, border: "none",
            background: "var(--float)", color: "var(--ink-1)",
            borderRadius: "var(--radius-panel)", boxShadow: "var(--shadow-dialog)",
        }, children: _jsxs("div", { style: { padding: "var(--space-35) var(--space-35) 22px" }, children: [_jsx("h2", { id: titleId, style: {
                        margin: "0 0 var(--space-1)", fontSize: "var(--type-title-size)",
                        lineHeight: "var(--type-title-line)", fontWeight: "var(--type-title-weight)",
                        letterSpacing: "var(--type-title-tracking-dialog)",
                    }, children: title }), children, _jsx("div", { style: {
                        display: "flex", justifyContent: "flex-end", gap: "var(--space-15)",
                        marginTop: "var(--space-3)",
                    }, children: actions })] }) }));
}
